import React, { useState, useEffect, useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { getData, putData } from '../helpers/ApiSettings';

// Valeurs initialement affichées sur ovlm97.fr/ateliers (avant que ce
// content-type ne soit utilisé). Pré-remplies au premier accès pour que la
// cliente puisse les modifier directement au lieu de tout re-saisir.
const INITIAL_HERO_TITLE = 'Nos ateliers de prévention';
const INITIAL_HERO_SUBTITLE = "Réservez en ligne et participez à nos ateliers de prévention pour apprendre les bons réflexes et profiter pleinement de vos enfants en toute sécurité.";
const INITIAL_INTRO_HTML = `<p>Nous proposons aux parents ou tout autres personnes ayant en charge un enfant des ateliers de prévention portant sur différentes thématiques. Animés par une passionnée pour des petits groupes de maximum 12 personnes, ces ateliers vous permettent de bénéficier de conseils adaptés et d'acquérir les bons réflexes pour éviter les accidents, dans un cadre convivial et dynamique.</p>
<p>Que vous soyez parents, grands-parents, nounous, tantes, oncles… la prévention est une démarche essentielle, adoptez quelques bons réflexes bénéfiques pour la sécurité de l'enfant.</p>`;

const WorkshopPageContent = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isFirstTimeSetup, setIsFirstTimeSetup] = useState(false);
  const [formData, setFormData] = useState({
    hero_title: INITIAL_HERO_TITLE,
    hero_subtitle: INITIAL_HERO_SUBTITLE,
    intro_html: INITIAL_INTRO_HTML,
  });
  const editorRef = useRef(null);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await getData('/workshop-page');
      if (response.data?.data) {
        // Contenu déjà sauvegardé une fois : on charge les valeurs réelles.
        setFormData({
          hero_title: response.data.data.hero_title || INITIAL_HERO_TITLE,
          hero_subtitle: response.data.data.hero_subtitle || INITIAL_HERO_SUBTITLE,
          intro_html: response.data.data.intro_html || INITIAL_INTRO_HTML,
        });
        setIsFirstTimeSetup(false);
      } else {
        setIsFirstTimeSetup(true);
      }
      setError(null);
    } catch (err) {
      if (err?.response?.status === 404) {
        // Premier accès : on garde les valeurs initiales (déjà dans le state).
        setIsFirstTimeSetup(true);
      } else {
        console.error('Error fetching workshop page:', err);
        setError('Erreur lors du chargement du contenu');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const introHtml = editorRef.current ? editorRef.current.getContent() : formData.intro_html;
      await putData('/workshop-page', {
        data: {
          hero_title: formData.hero_title,
          hero_subtitle: formData.hero_subtitle,
          intro_html: introHtml,
        },
      });
      alert('Contenu mis à jour avec succès');
    } catch (err) {
      alert('Erreur lors de la mise à jour');
      console.error('Error saving workshop page:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 mb-0">Page d'accueil ateliers</h2>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="alert alert-info">
        Cette page contrôle le contenu affiché sur <strong>ovlm97.fr/ateliers</strong> (titre, sous-titre, et bloc d'introduction). Modifiez les champs ci-dessous puis cliquez sur <strong>Enregistrer</strong> — les modifications seront visibles immédiatement sur le site.
      </div>

      {isFirstTimeSetup && (
        <div className="alert alert-warning">
          <strong>Première utilisation :</strong> les champs ci-dessous sont pré-remplis avec le texte actuellement affiché sur ovlm97.fr/ateliers. Vous pouvez le modifier puis enregistrer. Une fois enregistré, ce sont ces valeurs qui seront affichées sur le site (au lieu du texte par défaut).
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="hero_title" className="form-label">Titre principal</label>
              <input
                type="text"
                className="form-control"
                id="hero_title"
                value={formData.hero_title}
                onChange={(e) => setFormData(prev => ({ ...prev, hero_title: e.target.value }))}
                placeholder="Ex: Nos ateliers de prévention"
              />
            </div>

            <div className="mb-3">
              <label htmlFor="hero_subtitle" className="form-label">Sous-titre / phrase d'accroche</label>
              <textarea
                className="form-control"
                id="hero_subtitle"
                rows="2"
                value={formData.hero_subtitle}
                onChange={(e) => setFormData(prev => ({ ...prev, hero_subtitle: e.target.value }))}
                placeholder="Ex: Réservez en ligne et participez à nos ateliers..."
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Texte d'introduction</label>
              <Editor
                onInit={(evt, editor) => editorRef.current = editor}
                initialValue={formData.intro_html}
                init={{
                  height: 400,
                  menubar: true,
                  plugins: [
                    'advlist', 'autolink', 'lists', 'link', 'charmap', 'preview',
                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                    'insertdatetime', 'table', 'help', 'wordcount'
                  ],
                  toolbar: 'undo redo | blocks | ' +
                    'bold italic forecolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'removeformat | help',
                  content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                }}
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WorkshopPageContent;
